// src/mobile_pages/const/PhotoBoard.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Image,
  Calendar,
  User,
  Trash2
} from "lucide-react";
import { getPhotoBoardList, createPhotoBoard, deletePhotoBoard } from "../../api/const";

export default function PhotoBoard() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // 검색 필터
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");

  const fetchData = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const params = {
        project: projectId,
        [searchField]: q,
        page,
        page_size: pageSize,
      };

      const res = await getPhotoBoardList(projectId, params);
      setRows(res.results ?? res);
      setTotal(res.count ?? res.total ?? res.length);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId, page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  const handleCreate = async () => {
    try {
      const newBoard = await createPhotoBoard({
        project: projectId,
        title: "새 사진대지",
        photo_ids: []
      });
      navigate(`view/${newBoard.id}`);
    } catch {
      alert("생성에 실패했습니다.");
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      await deletePhotoBoard(id);
      fetchData();
    } catch {
      alert("삭제에 실패했습니다.");
    }
  };

  // 페이지네이션 계산
  const totalPages = Math.ceil(total / pageSize) || 1;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-8">
        {/* 헤더 */}
        <header className="mb-4">
          <div className="flex items-center justify-between gap-3 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">
              사진대지
            </h1>
            <button
              onClick={handleCreate}
              className="flex-shrink-0 flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-[13px] font-semibold text-white shadow-sm active:scale-[0.97] transition-transform"
            >
              <Plus size={16} strokeWidth={2.5} />
              새 문서
            </button>
          </div>
          <p className="mt-1.5 text-[13px] text-slate-500">
            총 {total}건의 사진대지가 등록되어 있습니다
          </p>
        </header>

        {/* 검색 영역 */}
        <section className="mb-4">
          <div className="flex items-center gap-1.5 rounded-xl border bg-white px-2 py-2 shadow-sm min-w-0">
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              className="flex-shrink-0 bg-transparent text-[11px] text-slate-600 font-medium outline-none border-r border-slate-200 pr-1"
            >
              <option value="title">제목</option>
              <option value="author">작성자</option>
            </select>
            <Search size={14} className="text-slate-400 flex-shrink-0" />
            <input
              className="flex-1 min-w-0 bg-transparent text-[13px] text-slate-800 placeholder-slate-400 outline-none"
              placeholder="검색어 입력"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <button
              onClick={handleSearch}
              className="flex-shrink-0 rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600 active:scale-95"
            >
              검색
            </button>
          </div>
        </section>

        {/* 목록 */}
        <section className="space-y-3">
          {loading ? (
            <p className="py-12 text-center text-[15px] text-slate-400">
              불러오는 중...
            </p>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center">
              <Image size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-[15px] text-slate-400">등록된 사진대지가 없습니다</p>
              <p className="text-xs text-slate-400 mt-1">새 사진대지를 등록해보세요</p>
            </div>
          ) : (
            rows.map((row) => (
              <article
                key={row.id}
                onClick={() => navigate(`view/${row.id}`)}
                className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm active:scale-[0.99] transition-transform cursor-pointer"
              >
                {/* 삭제 버튼 */}
                <button
                  onClick={(e) => handleDelete(e, row.id)}
                  className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors z-10"
                >
                  <Trash2 size={16} />
                </button>

                {/* 제목 */}
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Image size={20} className="text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0 pr-8">
                    <h3 className="text-[15px] font-semibold text-slate-800 line-clamp-2">
                      {row.title || "(제목 없음)"}
                    </h3>
                  </div>
                </div>

                {/* 메타 정보 */}
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-2 pl-13">
                  <span className="flex items-center gap-1">
                    <User size={12} />
                    {row.created_by || "-"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {row.date
                      ? new Date(row.date).toLocaleDateString("ko-KR")
                      : "-"}
                  </span>
                </div>

                {/* 선택 화살표 */}
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronRight size={20} className="text-slate-300" />
                </div>
              </article>
            ))
          )}
        </section>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <nav className="mt-6 flex items-center justify-center gap-2">
            <button
              disabled={!canPrev}
              onClick={() => setPage((p) => p - 1)}
              className={`flex h-9 w-9 items-center justify-center rounded-full border ${canPrev
                  ? "border-slate-300 text-slate-600 active:scale-95"
                  : "border-slate-100 text-slate-300"
                }`}
            >
              <ChevronLeft size={18} />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-colors ${page === pageNum
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 active:bg-slate-100"
                    }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              className={`flex h-9 w-9 items-center justify-center rounded-full border ${canNext
                  ? "border-slate-300 text-slate-600 active:scale-95"
                  : "border-slate-100 text-slate-300"
                }`}
            >
              <ChevronRight size={18} />
            </button>
          </nav>
        )}
      </main>
    </div>
  );
}
